import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { COMPOSITE_SLOPE_CLASSES, compositeWheelPixels, describeWheelPoint, wheelPointAt, } from '../utils/aspectSlopeComposite';
const RING_COUNT = COMPOSITE_SLOPE_CLASSES.length;
const DIVIDER_COLOR = 'rgba(0, 0, 0, 0.45)';
const MAX_PIXEL_RATIO = 3;
// Paints the wheel at pixelSize device pixels: the composite colours, then a
// thin divider between slope rings and around the rim.
const paintWheel = (canvas, hemisphere, pixelSize) => {
    canvas.width = pixelSize;
    canvas.height = pixelSize;
    const ctx = canvas.getContext('2d');
    if (!ctx)
        throw new Error('Canvas 2D context unavailable for the composite legend wheel');
    const image = ctx.createImageData(pixelSize, pixelSize);
    image.data.set(compositeWheelPixels(hemisphere, pixelSize));
    ctx.putImageData(image, 0, 0);
    const lineWidth = Math.max(1, pixelSize / 240);
    const centre = pixelSize / 2;
    const radius = centre - lineWidth / 2;
    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = lineWidth;
    for (let ring = 1; ring <= RING_COUNT; ring++) {
        ctx.beginPath();
        ctx.arc(centre, centre, (radius * ring) / RING_COUNT, 0, Math.PI * 2);
        ctx.stroke();
    }
};
// The wheel as a PNG data URL, for the print sheet (html2canvas captures an
// <img> exactly; a live canvas is not guaranteed to survive the export).
export const compositeWheelDataUrl = (hemisphere, pixelSize) => {
    const canvas = document.createElement('canvas');
    paintWheel(canvas, hemisphere, pixelSize);
    return canvas.toDataURL('image/png');
};
// N / E / S / W around a wheel of `size` px, inside a box `pad` px larger on
// every side.
export const CompassLetters = ({ size, pad, color, fontSize, }) => {
    const box = size + pad * 2;
    const base = {
        position: 'absolute',
        color,
        fontSize,
        lineHeight: 1,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
    };
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "ls-emphasis", style: { ...base, left: box / 2, top: pad / 2 }, children: "N" }), _jsx("span", { className: "ls-emphasis", style: { ...base, left: box - pad / 2, top: box / 2 }, children: "E" }), _jsx("span", { className: "ls-emphasis", style: { ...base, left: box / 2, top: box - pad / 2 }, children: "S" }), _jsx("span", { className: "ls-emphasis", style: { ...base, left: pad / 2, top: box / 2 }, children: "W" })] }));
};
const PAD = 18;
const READOUT_LINE = { lineHeight: '18px', height: 18 };
// The on-map composite legend: the wheel with a live readout. Pointing at any
// spot names the direction and slope class that colour stands for, so the user
// never has to decode a key.
export const CompositeWheelInteractive = ({ hemisphere, size = 176, }) => {
    const canvasRef = useRef(null);
    const [pointer, setPointer] = useState(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ratio = Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio || 1);
        paintWheel(canvas, hemisphere, Math.round(size * ratio));
    }, [hemisphere, size]);
    const read = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        const point = wheelPointAt(x, y, r.width);
        setPointer(point ? { x, y, point } : null);
    };
    // A tap keeps its reading after the finger lifts; a mouse clears on leave.
    const leave = (e) => {
        if (e.pointerType === 'mouse')
            setPointer(null);
    };
    const ringRadius = (ring) => ((size / 2) * ring) / RING_COUNT;
    const reading = pointer ? describeWheelPoint(pointer.point) : null;
    return (_jsxs("div", { className: "mt-2 flex flex-col items-center", children: [_jsxs("div", { className: "relative", style: { width: size + PAD * 2, height: size + PAD * 2 }, children: [_jsx(CompassLetters, { size: size, pad: PAD, color: "var(--sand)", fontSize: 12 }), _jsx("canvas", { ref: canvasRef, onPointerMove: read, onPointerDown: read, onPointerLeave: leave, className: "absolute rounded-full cursor-crosshair", style: { left: PAD, top: PAD, width: size, height: size, touchAction: 'none' }, "aria-label": "Aspect and slope composite color wheel", role: "img" }), pointer && (_jsxs("svg", { className: "absolute", style: { left: PAD, top: PAD, pointerEvents: 'none' }, width: size, height: size, "aria-hidden": true, children: [[pointer.point.classIndex, pointer.point.classIndex + 1]
                                .filter((ring) => ring > 0)
                                .map((ring) => (_jsx("circle", { cx: size / 2, cy: size / 2, r: ringRadius(ring), fill: "none", stroke: "white", strokeWidth: 1.5, style: { filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))' } }, ring))), _jsx("circle", { cx: pointer.x, cy: pointer.y, r: 3.5, fill: "white", stroke: "rgba(0,0,0,0.7)", strokeWidth: 1 })] }))] }), _jsxs("div", { className: "mt-1 w-full text-center", children: [_jsx("div", { className: "text-xs truncate", style: { ...READOUT_LINE, color: pointer ? 'var(--sand)' : 'var(--dew)' }, children: reading ? reading.facing : 'Point at the wheel to read a color' }), _jsx("div", { className: "text-xs truncate", style: { ...READOUT_LINE, color: 'var(--sand)' }, children: reading ? reading.slope : ' ' }), _jsx("div", { className: "mt-1 text-[10px] truncate", style: { color: 'var(--dew)', lineHeight: '14px', height: 14 }, children: "Color = the way the ground faces. Paler = flatter." })] })] }));
};
